import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1620033263019-f2ec2c738a60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxzaW5nYXBvcmUlMjBza3lsaW5lJTIwbW9ybmluZ3xlbnwwfHx8fDE3ODYxNzA5NTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email });
        toast.success("If the email exists, a reset link was generated (check server logs).");
        setForgot(false);
      } else {
        await login(email, password);
        navigate("/");
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[hsl(var(--sidebar))] text-white overflow-hidden">
        <img src={BG} alt="Singapore" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--sidebar))] via-[hsl(var(--sidebar))]/80 to-[hsl(var(--sidebar))]/40" />
        <div className="relative flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-destructive flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading font-extrabold text-2xl">OKRStaff</span>
        </div>
        <div className="relative">
          <h2 className="font-heading font-extrabold text-4xl leading-tight max-w-md">
            The complete HR command center for Singapore teams.
          </h2>
          <p className="mt-4 text-white/70 max-w-md text-sm leading-relaxed">
            Attendance, leave, payroll, performance and engagement — all in Singapore Time, all in one place.
          </p>
        </div>
        <p className="relative overline text-white/50">Internal use only · UTC+8</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-destructive flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-extrabold text-xl">OKRStaff</span>
          </div>
          <p className="overline">{forgot ? "Recover access" : "Welcome back"}</p>
          <h1 className="font-heading font-extrabold text-3xl mt-1 mb-8">
            {forgot ? "Reset password" : "Sign in"}
          </h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.sg"
                required
                className="mt-1.5"
              />
            </div>
            {!forgot && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1.5"
                />
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 mt-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>{forgot ? "Send reset link" : "Sign in"} <ArrowRight className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          </form>
          <button
            onClick={() => setForgot(!forgot)}
            className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="toggle-forgot"
          >
            {forgot ? "← Back to sign in" : "Forgot your password?"}
          </button>
        </div>
      </div>
    </div>
  );
}
